/*
  Shoppable video carousel.

  Uses the theme's bundled Swiper (assets/swiper.min.js) rather than adding a
  new dependency. Swiper's loop mode is what gives a genuine continuous loop —
  it clones slides so the track never rewinds when it reaches the end.

  Config comes from data attributes on the root so the section schema stays the
  single source of truth.
*/

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* How long the control lingers after playback starts, or after a tap. */
  var CONTROL_LINGER = 2200;

  /*
    Swiper builds its loop by cloning slides, and it needs a full track's worth
    on each side to do that. With fewer real slides than slidesPerView it either
    disables the loop or pads with blanks — so we repeat the real slides in the
    DOM first until there are comfortably enough. This is what lets a section
    with a single reel still loop continuously at any width.

    Duplicates must not carry ids: the reels contain a product form, and a
    repeated id would break both the DOM and the label associations. The
    theme's product-form.js binds to the <form> element itself, not to an id,
    so stripping them keeps add-to-cart working in every copy.
  */
  function padForLoop(wrapper, maxPerView) {
    var originals = Array.prototype.slice.call(wrapper.children);
    if (!originals.length) return;

    // Enough for a full view on each side of the active run, plus a margin.
    var required = Math.ceil(maxPerView) * 3;
    if (originals.length >= required) return;

    var index = 0;
    while (wrapper.children.length < required) {
      var copy = originals[index % originals.length].cloneNode(true);

      copy.removeAttribute('id');
      copy.querySelectorAll('[id]').forEach(function (node) {
        node.removeAttribute('id');
      });
      // These point at ids that no longer exist on the copy.
      copy.querySelectorAll('[for], [aria-labelledby], [aria-controls]').forEach(function (node) {
        node.removeAttribute('for');
        node.removeAttribute('aria-labelledby');
        node.removeAttribute('aria-controls');
      });
      copy.setAttribute('data-shoppable-duplicate', 'true');

      wrapper.appendChild(copy);
      index += 1;
    }
  }

  /*
    Reel playback, owned by this section.

    Only the slide Swiper reports as active plays. Everything else pauses,
    including the copies Swiper clones for the loop — comparing visibility
    across them is what made the wrong reel play and the active one stall.

    The control is deliberately never taken away. It fades back while the video
    runs so the footage is clear, and comes straight back on hover, on focus,
    on a tap, and on pause — a paused reel always shows its play button.
  */
  function Reel(media) {
    this.media = media;
    this.video = media.querySelector('video');
    if (!this.video) return;

    this.src = media.getAttribute('data-video-src') || '';
    this.autoplay = media.getAttribute('data-autoplay') !== 'false';
    this.playWithSound = media.getAttribute('data-muted') === 'false';
    this.attached = !this.src;
    this.pressed = false;
    this.idle = null;
    this.toggles = Array.prototype.slice.call(media.querySelectorAll('[data-viewport-video-toggle]'));

    this.video.playsInline = true;
    this.video.setAttribute('playsinline', '');
    if (this.autoplay) this.mute();

    var self = this;

    this.video.addEventListener('play', function () {
      media.setAttribute('data-playing', 'true');
      /*
        Latched, not tied to [data-playing]: the poster must stay gone once the
        footage has shown a frame. Bringing it back on pause looks exactly like
        the video resetting itself.
      */
      media.setAttribute('data-started', 'true');
      self.syncLabels();
      self.reveal(false);
    });

    this.video.addEventListener('pause', function () {
      media.setAttribute('data-playing', 'false');
      self.syncLabels();
      // Pin it on — this is the state the visitor needs a control in most.
      self.reveal(true);
    });

    this.video.addEventListener('ended', function () {
      media.setAttribute('data-playing', 'false');
      self.syncLabels();
      self.reveal(true);
    });

    this.toggles.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        self.press();
      });
    });

    /*
      Touch devices never hover, so a tap anywhere on the frame surfaces the
      control rather than leaving the visitor with nothing to aim at. Presses
      on the button itself are left to the handler above.
    */
    media.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse') return;
      if (event.target.closest && event.target.closest('[data-viewport-video-toggle]')) return;
      self.reveal(false);
    });

    media.reelPlayer = this;
  }

  Reel.prototype.mute = function () {
    this.video.muted = true;
    this.video.setAttribute('muted', '');
  };

  /* Announce the action the press performs, not a fixed string. */
  Reel.prototype.syncLabels = function () {
    var playing = !this.video.paused;
    this.toggles.forEach(function (button) {
      var next = button.getAttribute(playing ? 'data-label-pause' : 'data-label-play');
      if (next) button.setAttribute('aria-label', next);
    });
  };

  /*
    Show the control. `pin` keeps it up indefinitely (paused state); otherwise
    it lingers and then recedes, but only if the video is still running.
  */
  Reel.prototype.reveal = function (pin) {
    this.media.setAttribute('data-controls', 'true');
    window.clearTimeout(this.idle);
    if (pin) return;

    var self = this;
    this.idle = window.setTimeout(function () {
      if (!self.video.paused) self.media.setAttribute('data-controls', 'false');
    }, CONTROL_LINGER);
  };

  /* Assigning src begins loading; load() would abort a play already in flight. */
  Reel.prototype.attach = function () {
    if (this.attached) return;
    this.attached = true;
    this.video.src = this.src;
  };

  Reel.prototype.play = function () {
    if (!this.video.paused) return;
    this.attach();
    var self = this;
    var promise = this.video.play();
    if (!promise || !promise.catch) return;
    promise.catch(function () {
      if (self.video.muted) return;
      self.mute();
      var retry = self.video.play();
      if (retry && retry.catch) retry.catch(function () {});
    });
  };

  Reel.prototype.pause = function () {
    if (this.video.paused) return;
    this.video.pause();
  };

  Reel.prototype.press = function () {
    if (this.video.paused) {
      this.pressed = true;
      if (this.playWithSound) {
        this.video.muted = false;
        this.video.removeAttribute('muted');
      }
      this.play();
    } else {
      this.pressed = false;
      this.pause();
    }
  };

  /* Bind every reel in the section, clones included. */
  function bindReels(root) {
    root.querySelectorAll('[data-reel-video]').forEach(function (media) {
      if (media.reelPlayer) return;
      new Reel(media);
    });
  }

  /*
    Play the active slide, pause the rest. Called on init and every slide
    change, so the first active reel is handled exactly like any other.
  */
  function syncReels(root, swiper) {
    bindReels(root);

    var active = swiper.slides && swiper.slides[swiper.activeIndex];
    var activeMedia = active ? active.querySelector('[data-reel-video]') : null;

    root.querySelectorAll('[data-reel-video]').forEach(function (media) {
      var player = media.reelPlayer;
      if (!player) return;
      if (media === activeMedia) {
        if (player.autoplay || player.pressed) player.play();
      } else {
        player.pressed = false;
        player.pause();
      }
    });
  }

  /*
    Centre the side arrows on the active reel's media box.

    This cannot be a percentage: with 'Match the video' the media height comes
    from the footage's own ratio, so it is only known once that ratio has been
    applied — and it differs per reel and per breakpoint.
  */
  function positionArrows(root, swiper) {
    var arrows = root.querySelector('[data-shoppable-arrows]');
    if (!arrows) return;

    var slide = (swiper && swiper.slides && swiper.slides[swiper.activeIndex]) || root.querySelector('.swiper-slide');
    var media = slide && slide.querySelector('.shoppable-video__media');
    if (!media) return;

    var box = media.getBoundingClientRect();
    if (!box.height) return;

    var base = root.getBoundingClientRect();
    root.style.setProperty('--shoppable-arrow-top', box.top - base.top + box.height / 2 + 'px');
  }

  function build(root) {
    if (root.dataset.shoppableVideoReady === 'true') return;

    var container = root.querySelector('.swiper');
    if (!container || typeof window.Swiper === 'undefined') return;

    var perViewDesktop = parseFloat(root.getAttribute('data-per-view-desktop')) || 4;
    var perViewMobile = parseFloat(root.getAttribute('data-per-view-mobile')) || 1.2;
    var gap = parseInt(root.getAttribute('data-gap'), 10) || 16;
    var loop = root.getAttribute('data-loop') !== 'false';
    var autoScroll = root.getAttribute('data-auto-scroll') === 'true';
    var delay = parseInt(root.getAttribute('data-auto-scroll-delay'), 10) || 4000;

    var wrapper = container.querySelector('.swiper-wrapper');
    var maxPerView = Math.max(perViewDesktop, perViewMobile);

    if (loop && wrapper) {
      padForLoop(wrapper, maxPerView);
    }

    var options = {
      slidesPerView: perViewMobile,
      spaceBetween: gap,
      centeredSlides: root.getAttribute('data-center') === 'true',
      loop: loop,
      // We supply real duplicated slides above, so never let Swiper pad the
      // track with empty ones.
      /*
        Explicit: with rewind on, running past the last reel snaps the whole
        track back to the start instead of carrying on, which is the jump that
        reads as the carousel only moving one way. Looping handles the wrap.
      */
      rewind: false,
      loopAddBlankSlides: false,
      loopAdditionalSlides: Math.ceil(maxPerView),
      watchSlidesProgress: true,
      breakpoints: {
        750: {
          slidesPerView: perViewDesktop,
          spaceBetween: gap,
        },
      },
    };

    if (root.querySelector('[data-shoppable-prev]')) {
      options.navigation = {
        prevEl: root.querySelector('[data-shoppable-prev]'),
        nextEl: root.querySelector('[data-shoppable-next]'),
        disabledClass: 'swiper-button-disabled',
      };
    }

    // Honour reduced-motion by never auto-advancing.
    if (autoScroll && !REDUCED.matches) {
      options.autoplay = {
        delay: delay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    /*
      Swiper moves the track with a transform, so the shared scroll-slider
      progress logic (which reads scrollLeft) cannot drive this bar. Paint it
      from Swiper's own normalised progress instead.
    */
    var bar = root.querySelector('[data-scroll-slider-progress-bar]');
    var paint = function (s) {
      if (!bar) return;
      var visible = Math.min(1, s.params.slidesPerView / Math.max(1, s.slides.length));
      var width = visible * 100;
      bar.style.width = width + '%';
      bar.style.transform = 'translateX(' + s.progress * ((100 / width) * 100 - 100) + '%)';
    };

    var sync = function (swiper) {
      syncReels(root, swiper);
      positionArrows(root, swiper);
    };

    /* Layout-only pass: no playback churn, so it is safe on every resize. */
    var relayout = function (s) {
      paint(s);
      positionArrows(root, s);
    };

    options.on = {
      init: function (s) {
        paint(s);
        sync(s);
      },
      progress: paint,
      resize: relayout,
      // Crossing a breakpoint changes slidesPerView, and with it the bleed.
      breakpoint: relayout,
      slideChange: sync,
      slideChangeTransitionEnd: sync,
    };

    var swiper = new window.Swiper(container, options);
    root.dataset.shoppableVideoReady = 'true';
    root.swiperInstance = swiper;

    /*
      The media box settles later than init: posters decode, and 'Match the
      video' only resolves its ratio once the image dimensions are known.
      Re-measure when the track's own size actually changes.
    */
    if (typeof window.ResizeObserver !== 'undefined') {
      var observer = new window.ResizeObserver(function () {
        positionArrows(root, swiper);
      });
      observer.observe(container);
      root.shoppableObserver = observer;
    }

    window.addEventListener('load', function () {
      positionArrows(root, swiper);
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-shoppable-video]').forEach(build);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init.bind(null, undefined));

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-shoppable-video]').forEach(function (el) {
      if (el.shoppableObserver) el.shoppableObserver.disconnect();
      if (el.swiperInstance) el.swiperInstance.destroy(true, true);
    });
  });
})();
